import Axios from "axios";
import useAuthUser from "hooks/auth-user";
import useEngageStatus from "hooks/useEngageStatus";
import { DateTime } from "luxon";
import {
    FollowUpType,
    PREP_BLACKOUT_INTERVAL_IN_HOURS,
} from "models/appointment";
import {
    hasPrepEligibilityPA,
    hasPrepInterest,
    isPrepEligible,
} from "models/user";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { getLabType } from "utils/lab-type-helper";
import { getPreSelectedClinicID } from "utils/local-storage";
import { getPreSelectedLabType } from "utils/session-storage";
import { Button, LoadingIcon } from "../../../components";
import ClinicList from "../../../components/clinic-list";
import useLanguage from "../../../hooks/language";
import Clinic from "../../../models/clinic";
import ENDPOINTS from "../../../utils/endpoints";
import { FetchClinicsBody } from "../../availability/default/index";
import Prep from "./prep";

interface DateSelectionProps {
    onSubmit?: (
        isoDate: string,
        clinicId: number,
        prepDate: string | null,
        isPrepRefused?: boolean,
        clinicName?: string
    ) => void;
    fetchClinicsBody?: FetchClinicsBody;
    showTitle?: boolean;
    loading?: boolean;
    selectedFollowUpType: FollowUpType | undefined;
    trackBookingFlowClinicListFilter?: boolean;
}

const DateSelection: React.FunctionComponent<DateSelectionProps> = ({
    onSubmit,
    loading = false,
    selectedFollowUpType,
    fetchClinicsBody,
    showTitle = true,
    trackBookingFlowClinicListFilter = false,
}: DateSelectionProps) => {
    const { translations } = useLanguage();
    const t = translations.dateSection;
    const [authUser] = useAuthUser();
    const { isEngageParticipationActive } = useEngageStatus();
    // States

    const [isoDate, setIsoDate] = useState<string | null>(null);
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [isLoadingClinics, setIsloadingClinics] = useState<boolean>(false);
    const [selectedClinicID, setSelectedClinicID] = useState<number | null>(
        getPreSelectedClinicID
    );
    const [expandedClinicID, setExpandedClinicID] = useState<number | null>(
        null
    );
    const [wantsPrep, setWantsPrep] = useState<boolean | undefined>();
    const [prepEligibilityDate, setPrepEligibilityDate] = useState<
        string | null
    >(null);
    const [samplingStartDate, setSamplingStartDate] = useState<
        string | undefined
    >(undefined);

    const canChoosePrepDate = authUser
        ? hasPrepInterest(authUser) &&
          !isPrepEligible(authUser) &&
          !hasPrepEligibilityPA(authUser)
        : false;
    const hasMadePrepDecision =
        wantsPrep === false || (wantsPrep && prepEligibilityDate);

    // Effects

    useEffect(() => {
        if (!canChoosePrepDate || prepEligibilityDate || wantsPrep === false)
            fetchClinics();
    }, [canChoosePrepDate, wantsPrep, prepEligibilityDate, fetchClinicsBody]);

    // Networking

    const fetchClinics = async () => {
        try {
            const startDate = prepEligibilityDate
                ? DateTime.fromISO(prepEligibilityDate)
                      .plus({
                          hour: PREP_BLACKOUT_INTERVAL_IN_HOURS,
                      })
                      .toISO()
                : undefined;
            setSamplingStartDate(startDate);
            setIsloadingClinics(true);

            const labType = getLabType(getPreSelectedLabType());
            const body = fetchClinicsBody ?? {
                isEngageAppointment: isEngageParticipationActive,
                originPostalCode: authUser?.zipCode?.replace(/\s+/g, ""),
                labType,
            };
            const { data } = await Axios.post<Clinic[]>(
                ENDPOINTS.CLINICS,
                body
            );

            setIsloadingClinics(false);
            setClinics(data);
        } catch (error) {
            console.log(error);
            NotificationManager.error(error);
        }
    };

    // Handlers

    const _onSubmit = () => {
        const isPrepRefused = canChoosePrepDate ? !wantsPrep : undefined;

        if (onSubmit != null && isoDate != null && selectedClinicID != null) {
            const selectedClinic = clinics.find(
                (clinic) => clinic.id === selectedClinicID
            );
            onSubmit(
                isoDate,
                selectedClinicID,
                prepEligibilityDate,
                isPrepRefused,
                selectedClinic?.name
            );
        }
    };

    const handleClinicSelection = (clinicId: number | null) => {
        setSelectedClinicID(clinicId);
        setExpandedClinicID(clinicId);
    };

    // Rendering

    return (
        <div>
            {showTitle && canChoosePrepDate && (
                <>
                    <h2>{t.titleWithPrep}</h2>
                    <Prep
                        setPrepEligibilityDate={setPrepEligibilityDate}
                        wantsPrep={wantsPrep}
                        setWantsPrep={setWantsPrep}
                    />
                </>
            )}
            {showTitle && !canChoosePrepDate && <h2>{t.header}</h2>}
            {showTitle && authUser?.zipCode && (
                <p>
                    {translations.appointmentConfirmation.fastestNearPostalCode}
                    <span style={{ textDecoration: "underline" }}>
                        {authUser?.zipCode.toUpperCase()}
                    </span>
                </p>
            )}
            {(!canChoosePrepDate || hasMadePrepDecision) && (
                <>
                    <div
                        style={{
                            display: clinics.length > 0 ? "block" : "none",
                        }}
                    >
                        {canChoosePrepDate && (
                            <>
                                <h3>{t.screeningSectionTitle}</h3>
                                <p>{t.screeningSectionDescription}</p>
                            </>
                        )}
                    </div>
                    {isLoadingClinics ? (
                        <>
                            <LoadingIcon /> <span>{t.clinicsAreLoading}</span>
                        </>
                    ) : (
                        <>
                            <ClinicList
                                clinics={clinics}
                                onDateSelect={setIsoDate}
                                selectedClinicID={expandedClinicID}
                                setSelectedClinicID={handleClinicSelection}
                                startDate={samplingStartDate}
                                style={{
                                    marginLeft: canChoosePrepDate
                                        ? "8px"
                                        : "0px",
                                }}
                                selectedFollowUpType={selectedFollowUpType}
                                trackBookingFlowClinicListFilter={
                                    trackBookingFlowClinicListFilter
                                }
                            />
                            <div className="dual-button-holder flex-end sticky-button-holder">
                                <div className="sticky-button-wrapper">
                                    <Button
                                        disabled={!isoDate || !selectedClinicID}
                                        onClick={_onSubmit}
                                        loading={loading}
                                    >
                                        {t.submitButtonText}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
            <br />
        </div>
    );
};

export const Hr: React.FunctionComponent<React.HTMLProps<HTMLHRElement>> = (
    props: React.HTMLProps<HTMLHRElement>
) => <hr style={{ borderTop: "1px solid #BFBFBF" }} {...props} />;

export default DateSelection;
